import React, { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { ArrowRight, Users, CheckCircle, Buildings, Phone, Envelope, MapPin } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Field({ Icon, label, k, type = "text", placeholder, value, onChange, testPrefix }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">
        {label}
      </label>
      <div className="flex items-center gap-2 h-10 px-3 border border-[#E5E2D9] rounded-md focus-within:border-[#D23247] transition-colors">
        <Icon size={14} className="text-slate-400 shrink-0"/>
        <input type={type} value={value} onChange={onChange} placeholder={placeholder}
               data-testid={`${testPrefix}-reg-${k}`}
               className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"/>
      </div>
    </div>
  );
}

const FIELDS = [
  { Icon: Buildings, label: "Company Name",            k: "company", placeholder: "Enter your company name" },
  { Icon: Users,     label: "Contact Person",          k: "name",    placeholder: "Enter name" },
  { Icon: Phone,     label: "Mobile Number",           k: "phone",   placeholder: "Enter mobile number", type: "tel" },
  { Icon: Envelope,  label: "Email Address",           k: "email",   placeholder: "Enter email address", type: "email" },
  { Icon: MapPin,    label: "City / Service Location", k: "city",    placeholder: "Thiruvananthapuram, Kerala" },
];

export function PartnerRegisterForm({
  compact = false, category, tag, testPrefix, title, subtitle, defaultMessage, successMsg,
}) {
  const empty = { name: "", company: "", phone: "", email: "", city: "",
                  message: defaultMessage, category, website: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.name || !form.company || !form.phone || !form.email || !form.city) {
      toast.error("Please fill in all fields");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${BACKEND}/api/public/partner-enquiry`,
        { ...form, message: `[${tag}] ${form.message}` }, { timeout: 15000 });
      setDone(true); setForm(empty);
      toast.success(successMsg);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      toast.error(typeof d === "string" ? d :
                  Array.isArray(d) ? d.map(x => x.msg).join("; ") :
                  "Could not submit, please try again");
    }
    setBusy(false);
  };

  if (done) {
    return (
      <div data-testid={`${testPrefix}-reg-done`}
           className="rounded-2xl bg-[#DCFCE7] border border-[#86EFAC] p-6 text-center">
        <CheckCircle size={36} weight="duotone" className="text-[#15803D] mx-auto"/>
        <h3 className="font-heading text-lg mt-2 text-slate-900">Thanks!</h3>
        <p className="text-sm text-slate-700 mt-1">We'll reach out within one business day.</p>
        <button onClick={() => setDone(false)} data-testid={`${testPrefix}-reg-again`}
                className="mt-3 text-xs font-medium text-[#D23247] hover:underline">
          Register another company
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${testPrefix}-register-form`}
          className={`relative bg-white rounded-2xl border border-[#F0EEEA] p-5 sm:p-6 ${compact ? "" : "shadow-xl"}`}>
      <h3 className="font-heading text-xl text-slate-900">{title}</h3>
      <p className="text-[12px] text-slate-500 mt-0.5">{subtitle}</p>
      <input tabIndex={-1} autoComplete="off" value={form.website} name="website"
             onChange={update("website")} aria-hidden="true"
             className="absolute left-[-9999px] opacity-0 pointer-events-none"/>
      <div className="mt-4 space-y-3">
        {FIELDS.map(f => (
          <Field key={f.k} {...f} value={form[f.k]} onChange={update(f.k)} testPrefix={testPrefix}/>
        ))}
      </div>
      <button type="submit" disabled={busy} data-testid={`${testPrefix}-register-submit`}
              className="mt-5 h-12 w-full inline-flex items-center justify-center gap-2 bg-[#D23247] hover:bg-[#B52236] text-white text-sm font-semibold rounded-md disabled:opacity-60 transition-colors">
        {busy ? "Sending…" : "Get Started"} <ArrowRight size={14} weight="bold"/>
      </button>
      <p className="text-[11px] text-slate-500 mt-3">
        This is the first step. You can complete the full registration,
        document upload and service details in the next steps.
      </p>
    </form>
  );
}
